// Service Worker utility for handling PWA registration and updates
export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateAvailable = false;

  async register(): Promise<void> {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      try {
        // Register the service worker with error handling
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('Service Worker registered successfully');

        // Handle updates
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration?.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.updateAvailable = true;
                this.notifyUpdateAvailable();
              }
            });
          }
        });

        // Handle controller changes
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.location.reload();
        });

      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  async updateServiceWorker(): Promise<void> {
    if (this.registration && this.updateAvailable) {
      try {
        await this.registration.update();
        
        // Skip waiting to activate new service worker immediately
        if (this.registration.waiting) {
          this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      } catch (error) {
        console.error('Failed to update service worker:', error);
      }
    }
  }

  private notifyUpdateAvailable(): void {
    // Dispatch a custom event that can be caught by components
    const event = new CustomEvent('sw-update-available', {
      detail: { updateAvailable: true }
    });
    window.dispatchEvent(event);
  }

  async unregister(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
        console.log('Service Worker unregistered successfully');
      } catch (error) {
        console.error('Service Worker unregistration failed:', error);
      }
    }
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }
}

// Create a singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Auto-register on module load
if (typeof window !== 'undefined') {
  serviceWorkerManager.register();
} 